import { apiClient } from '@/helpers/api.js'
import { useCardStore } from '@/stores/CardStore'
import { handleApiError } from '@/services/errorHandler'
import { applyMarketPricesToCards, applyMarketPriceToCard } from '@/helpers/inventory.js'

/**
 * Unified market-price layer shared by Inventory and Batch.
 *
 * Both surfaces render the same `cardStore.cards` and both endpoints return the
 * same `{ ok, recommendations }` shape (a map keyed by card id), so everything
 * except the URL/params is identical:
 *   Inventory: GET /inventory/{id}/market-prices   (filters/pagination in the url)
 *   Batch:     GET /batches/{batchId}/market-prices (same filters/pagination as
 *              the cards list, passed as axios `params`)
 */

/**
 * Merge a recommendations map (`{ [cardId]: { suggested_price, last_updated } }`)
 * into whatever cards are currently loaded — the list AND the open detail card.
 * Idempotent, and a no-op until there are both prices and cards to merge.
 */
export const applyMarketPricesToCardStore = (marketPrices) => {
  if (!marketPrices) return

  const cardStore = useCardStore()

  if (cardStore.cards?.length) {
    cardStore.cards = applyMarketPricesToCards(cardStore.cards, marketPrices)
  }

  if (cardStore.selectedCardData?.id) {
    cardStore.selectedCardData = applyMarketPriceToCard(cardStore.selectedCardData, marketPrices)
  }
}

/**
 * Fetch market prices into a store. Aborts the store's previous in-flight
 * request (own controller, so it never fights the cards-list request), GETs
 * `url` (filters may live in the url OR in `params`), and on `{ ok }` stores the
 * recommendations on the store and merges them into the card store. Aborted /
 * canceled requests resolve to `null` without touching state.
 *
 * @param {Object}  store            Pinia store instance holding the controller + prices
 * @param {Object}  opts
 * @param {String}  opts.url         market-prices endpoint
 * @param {Object} [opts.params]     axios params (Batch passes the cards-list query here)
 * @param {String} [opts.context]    component label for error reporting
 * @param {String} [opts.controllerKey='currentMarketPriceController']
 * @param {String} [opts.pricesKey='marketPrices']
 * @returns {Promise<Object|null>} the recommendations map, or null
 */
export const fetchMarketPricesInto = async (
  store,
  { url, params, context = 'marketPrices', controllerKey = 'currentMarketPriceController', pricesKey = 'marketPrices' },
) => {
  try {
    if (store[controllerKey]) store[controllerKey].abort()

    const controller = new AbortController()
    store[controllerKey] = controller

    const { data } = await apiClient().get(url, { params, signal: controller.signal })

    // Bail if this request was superseded/canceled.
    if (controller.signal.aborted) return null

    if (data?.ok) {
      store[pricesKey] = data.recommendations
      applyMarketPricesToCardStore(data.recommendations)
    } else {
      store[pricesKey] = null
      handleApiError(
        { message: data?.message || 'Failed to fetch market prices' },
        { component: context, action: 'fetchMarketPrices' },
      )
    }

    store[controllerKey] = null
    return store[pricesKey]
  } catch (error) {
    if (['AbortError', 'CanceledError'].includes(error.name) || error.code === 'ERR_CANCELED') return null

    handleApiError(error, { component: context, action: 'fetchMarketPrices' })
    store[controllerKey] = null
    return null
  }
}
